import type { ParticipantEditFieldState } from '../../selectors'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { DateInput } from '@/scopes/common/components/FormElements/DateInput'
import { RadioButtonInput } from '@/scopes/common/components/FormElements/RadioButtonInput'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { fieldProps, toRadioItems } from '../../utils/participantEdit/participantEditFields'
import styles from './ParticipantEditShared.module.css'

interface ParticipantEditPersonalSectionProps {
  fields: {
    title: ParticipantEditFieldState | undefined
    gender: ParticipantEditFieldState | undefined
    firstName: ParticipantEditFieldState | undefined
    lastName: ParticipantEditFieldState | undefined
    dateOfBirth: ParticipantEditFieldState | undefined
  }
}

export function ParticipantEditPersonalSection({ fields }: ParticipantEditPersonalSectionProps) {
  const { formatMessage } = useIntl()
  const dateFormat = useConfiguration('dateFormat')
  const { title, gender, firstName, lastName, dateOfBirth } = fields

  if (!title && !gender && !firstName && !lastName && !dateOfBirth) {
    return null
  }

  return (
    <div className={styles.section}>
      <Typography as="h2" variant="display-h-regular">
        {formatMessage({ id: 'booking_details_participant_edit_personal_info_section_title' })}
      </Typography>
      <div className={styles.fieldsStack}>
        {title && (
          <RadioButtonInput
            name="title"
            label={formatMessage({ id: 'booking_details_participant_edit_title_label' })}
            items={toRadioItems('title', title.options, formatMessage)}
            {...fieldProps(title, formatMessage)}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_salutation_required_error' })}
          />
        )}
        {gender && (
          <RadioButtonInput
            name="gender"
            label={formatMessage({ id: 'booking_details_participant_edit_gender_label' })}
            items={toRadioItems('gender', gender.options, formatMessage)}
            {...fieldProps(gender, formatMessage)}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_salutation_required_error' })}
          />
        )}
        {firstName && (
          <TextInput
            name="firstName"
            label={formatMessage({ id: 'booking_details_participant_edit_first_name_label' })}
            {...fieldProps(firstName, formatMessage, 'booking_details_participant_edit_first_name_help_text')}
            pattern={firstName.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_first_name_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_first_name_required_error' })}
          />
        )}
        {lastName && (
          <TextInput
            name="lastName"
            label={formatMessage({ id: 'last_name_label' })}
            {...fieldProps(lastName, formatMessage, 'booking_details_participant_edit_last_name_help_text')}
            pattern={lastName.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_last_name_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_last_name_required_error' })}
          />
        )}
        {dateOfBirth && (
          <DateInput
            name="dateOfBirth"
            label={formatMessage({ id: 'common_birthdate_label' })}
            dateRange={{ min: dateOfBirth.minValue, max: dateOfBirth.maxValue }}
            dateFormat={dateFormat}
            {...fieldProps(dateOfBirth, formatMessage)}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_birthdate_required_error' })}
            validateErrorMessage={formatMessage({ id: 'booking_details_participant_edit_birthdate_validate_error' })}
          />
        )}
      </div>
    </div>
  )
}
